import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// V70.224 — Oturum düşünce SessionExpiredModal (kayıtlıysa) sayfayı yenilemeden yeniden
// giriş ister; token'ı reddedilen istekler girişten sonra tekrar gönderilir.
const AUTH_URL = /\/auth\/(login|mfa|email-otp|cikis)/;
let sessionExpiredHandler = null;
let reauthQueue = [];
export const setSessionExpiredHandler = (fn) => { sessionExpiredHandler = fn; };
export const finishReauth = (ok) => {
  const q = reauthQueue;
  reauthQueue = [];
  q.forEach(w => (ok ? w.resolve() : w.reject()));
};

// Token interceptor + cache prevention
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mtu_token');
  if (token) { config.headers.Authorization = `Bearer ${token}`; config._token = token; }
  else if (sessionExpiredHandler && !AUTH_URL.test(config.url || '')) {
    // Yeniden giriş bekleniyor — arka plan istekleri sunucuya gitmesin
    return Promise.reject(Object.assign(new Error('Oturum süresi doldu'), {
      config, sessionPaused: true, response: { status: 401, data: { error: 'Oturum süresi doldu. Tekrar giriş yapın.' } },
    }));
  }
  // Prevent browser/CDN from caching authenticated API responses
  config.headers['Cache-Control'] = 'no-cache, no-store';
  config.headers['Pragma'] = 'no-cache';
  return config;
});

// Response interceptor - 401 redirect (login hariç)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthRequest = AUTH_URL.test(error.config?.url || '');
    const status = error.response?.status;
    const msg = error.response?.data?.error || '';
    // 401 → always logout; 403 → only if token-related (not permission denied)
    const isTokenExpired = status === 403 && msg.includes('token');
    if ((status === 401 || isTokenExpired) && !isAuthRequest) {
      if (sessionExpiredHandler) {
        if (error.config?._reauth) return Promise.reject(error);
        const sent = error.config?._token;
        if (sent && localStorage.getItem('mtu_token') === sent) localStorage.removeItem('mtu_token');
        // Başka sekmede yeniden giriş yapılmışsa yeni token ile hemen tekrar dene
        if (localStorage.getItem('mtu_token')) return api({ ...error.config, _reauth: true });
        sessionExpiredHandler();
        if (error.sessionPaused) return Promise.reject(error);
        return new Promise((resolve, reject) => reauthQueue.push({ resolve, reject }))
          .then(() => api({ ...error.config, _reauth: true }), () => Promise.reject(error));
      }
      localStorage.removeItem('mtu_token');
      localStorage.removeItem('mtu_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// V71 — <img src>/<a href> Authorization başlığı taşımaz (korumalı uç 401 döner). Personel fotoğrafı,
// belge vb. dosyayı token'lı istekle blob olarak çekip object URL döndürür; '/api/...' ya da
// API_BASE ile başlayan tam yol kabul edilir. Kullanan bileşen işi bitince URL.revokeObjectURL çağırır.
export const fetchAuthBlobUrl = async (url) => {
  const path = url.startsWith(`${API_BASE}/`) ? url.slice(API_BASE.length) : url.replace(/^\/api(?=\/)/, '');
  const res = await api.get(path, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  verifyMFA: (data) => api.post('/auth/mfa/verify', data),
  verifyEmailOtp: (data) => api.post('/auth/email-otp/verify', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  // MFA Setup (pending_token is optional — used for forced setup during login)
  mfaSetupStart: (pendingToken) => api.post('/auth/mfa/setup/start', pendingToken ? { pending_token: pendingToken } : {}),
  mfaSetupVerify: (code, pendingToken) => api.post('/auth/mfa/setup/verify', pendingToken ? { code, pending_token: pendingToken } : { code }),
  mfaDisable: (password, code) => api.post('/auth/mfa/disable', { password, code }),
  mfaStatus: () => api.get('/auth/mfa/status'),
  requestAccount: (data) => api.post('/auth/request-account', data),
  activate: (data) => api.post('/auth/activate', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  // V71.3.2 — üst çubukta çalışma şekli (Ofis / Remote)
  getWorkType: () => api.get('/auth/work-type'),
  setWorkType: (workType) => api.put('/auth/work-type', { work_type: workType }),
  // V71.42 — çıkış kaydı (token çıkıştan önce alınır; istek beklenmez)
  cikis: (token) => api.post('/auth/cikis', {}, { headers: { Authorization: `Bearer ${token}` } }),
  // V71.42 — Operasyon Haritası'ndaki kendi avatarı
  getAvatar: () => api.get('/auth/avatar'),
  setAvatar: (avatar) => api.put('/auth/avatar', { avatar }),
};

// Passengers
export const passengerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  import: (customers) => api.post('/customers/import', { customers }),
  importPreview: (passengers) => api.post('/customers/import/preview', { passengers }),
  getPassportExpiring: () => api.get('/customers/passport-expiring'),
};

// Drafts (Staging)
export const draftAPI = {
  getAll: (params) => api.get('/customers/drafts/list', { params }),
  getStats: () => api.get('/customers/drafts/stats'),
  update: (id, data) => api.put(`/customers/drafts/${id}`, data),
  delete: (id) => api.delete(`/customers/drafts/${id}`),
  approve: (ids) => api.post('/customers/drafts/approve', { ids }),
  approveAll: () => api.post('/customers/drafts/approve', { approve_all: true }),
  reject: (ids) => api.post('/customers/drafts/reject', { ids }),
  rejectAll: () => api.post('/customers/drafts/reject', { reject_all: true }),
  bulkDelete: (status) => api.post('/customers/drafts/bulk-delete', { status }),
};

// Tickets
export const ticketAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getExpiring: () => api.get('/tickets/expiring'),
  getById: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  bulkDelete: (ids) => api.post('/tickets/bulk-delete', { ids }),
  importPreview: (tickets) => api.post('/tickets/import/preview', { tickets }),
  importBulk: (tickets) => api.post('/tickets/import', { tickets }),
};

// Marketing
export const marketingAPI = {
  getAll: (params) => api.get('/marketing', { params }),
  getStats: () => api.get('/marketing/stats'),
  getById: (id) => api.get(`/marketing/${id}`),
  create: (data) => api.post('/marketing', data),
  update: (id, data) => api.put(`/marketing/${id}`, data),
  delete: (id) => api.delete(`/marketing/${id}`),
};

// Offers
export const offerAPI = {
  getAll: (params) => api.get('/offers', { params }),
  getById: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers', data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`),
  fromLead: (leadId) => api.post(`/offers/from-lead/${leadId}`),
  getPdfUrl: (id) => `${API_BASE}/offers/${id}/pdf`,
};

// Calendar
export const calendarAPI = {
  getMonth: (year, month) => api.get(`/calendar/month/${year}/${month}`),
  getById: (id) => api.get(`/calendar/${id}`),
  create: (data) => api.post('/calendar', data),
  update: (id, data) => api.put(`/calendar/${id}`, data),
  delete: (id) => api.delete(`/calendar/${id}`),
  syncMarketing: () => api.post('/calendar/sync-marketing'),
  getUsers: () => api.get('/calendar/users/list'),
  getLeads: () => api.get('/calendar/leads/list'),
  createFair: (data) => api.post('/calendar/fairs', data),
  updateFair: (id, data) => api.put(`/calendar/fairs/${id}`, data),
  deleteFair: (id) => api.delete(`/calendar/fairs/${id}`),
  fairSources: () => api.get('/calendar/fair-sources'),
  addFairSource: (url) => api.post('/calendar/fair-sources', { url }),
  deleteFairSource: (id) => api.delete(`/calendar/fair-sources/${id}`),
  runFairSources: (id) => api.post('/calendar/fair-sources/run', id ? { id } : {}),
};

// Companies
export const companyAPI = {
  getAll: (params) => api.get('/companies', { params }),
  lookup: () => api.get('/companies/lookup'),
  getPendingCount: () => api.get('/companies/pending-count'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  importPreview: (companies) => api.post('/companies/import/preview', { companies }),
  importMatch: (names) => api.post('/companies/import/match', { names }),
  import: (companies) => api.post('/companies/import', { companies }),
  sync: () => api.post('/companies/sync'),
  getSimilar: (threshold) => api.get('/companies/similar', { params: { threshold } }),
  merge: (target_id, source_ids) => api.post('/companies/merge', { target_id, source_ids }),
  bulkStatus: (ids, is_active) => api.post('/companies/bulk-status', { ids, is_active }),
  bulkType: (ids, company_type) => api.post('/companies/bulk-type', { ids, company_type }),
  bulkSector: (ids, sector) => api.post('/companies/bulk-sector', { ids, sector }),
  bulkParent: (ids, parent_id) => api.post('/companies/bulk-parent', { ids, parent_id }),
  getSectors: () => api.get('/companies/sectors'),
  // Cari eşleştirme
  getCariUnmatched: () => api.get('/companies/cari-match/unmatched'),
  linkCari: (cari_id, company_id) => api.post('/companies/cari-match/link', { cari_id, company_id }),
  createFromCari: (cari_id) => api.post('/companies/cari-match/create', { cari_id }),
  autoMatchCari: () => api.post('/companies/cari-match/auto'),
  uploadLogo: (id, logo_base64) => api.post(`/companies/${id}/logo`, { logo_base64 }),
  deleteLogo: (id) => api.delete(`/companies/${id}/logo`),
  // Vade bekleyenler
  getVadePending: () => api.get('/companies/vade-pending'),
  linkVade: (vade_id, company_id) => api.post('/companies/vade-pending/link', { vade_id, company_id }),
  createFromVade: (vade_id, vade_gun) => api.post('/companies/vade-pending/create', { vade_id, vade_gun }),
  setVade: (company_id, vade_gun) => api.post('/companies/vade-pending/set-vade', { company_id, vade_gun }),
};

// Hotel Rates
export const hotelRateAPI = {
  getAll: (params) => api.get('/hotel-rates', { params }),
  getCities: () => api.get('/hotel-rates/cities'),
  getStats: () => api.get('/hotel-rates/stats'),
  getById: (id) => api.get(`/hotel-rates/${id}`),
  create: (data) => api.post('/hotel-rates', data),
  update: (id, data) => api.put(`/hotel-rates/${id}`, data),
  delete: (id) => api.delete(`/hotel-rates/${id}`),
  import: (data) => api.post('/hotel-rates/import', data),
  getImportHistory: () => api.get('/hotel-rates/imports/history'),
  getReviewQueue: (params) => api.get('/hotel-rates/review-queue', { params }),
  resolveReview: (id, data) => api.put(`/hotel-rates/review-queue/${id}/resolve`, data),
  refreshStatuses: () => api.post('/hotel-rates/refresh-statuses'),
};

// Free Night Credits
export const freeNightAPI = {
  getAll: (params) => api.get('/free-night-credits', { params }),
  getStats: () => api.get('/free-night-credits/stats'),
  getById: (id) => api.get(`/free-night-credits/${id}`),
  create: (data) => api.post('/free-night-credits', data),
  update: (id, data) => api.put(`/free-night-credits/${id}`, data),
  delete: (id) => api.delete(`/free-night-credits/${id}`),
  useNight: (id, nights) => api.post(`/free-night-credits/${id}/use`, { nights }),
};

// Payments
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getStats: () => api.get('/payments/stats'),
  getToday: () => api.get('/payments/today'),
  getCalendar: (year, month) => api.get('/payments/calendar', { params: { year, month } }),
  getExchangeRates: () => api.get('/payments/exchange-rates'),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  pay: (id, data) => api.post(`/payments/${id}/pay`, data),
  getTransactions: (id) => api.get(`/payments/${id}/transactions`),
  getOptionDetails: (date) => api.get('/payments/option-details', { params: { date } }),
};

// Checkins
export const checkinAPI = {
  getAll: (params) => api.get('/checkins', { params }),
  getStats: () => api.get('/checkins/stats'),
  getUpcoming: () => api.get('/checkins/upcoming'),
  getCalendar: (year, month) => api.get('/checkins/calendar', { params: { year, month } }),
  importPreview: (rows) => api.post('/checkins/import/preview', { rows }),
  importBulk: (checkins, clearDate) => api.post('/checkins/import', { checkins, clear_date: clearDate }),
  complete: (id) => api.post(`/checkins/${id}/complete`),
  uncomplete: (id) => api.post(`/checkins/${id}/uncomplete`),
  delete: (id) => api.delete(`/checkins/${id}`),
  getArchive: (params) => api.get('/checkins/archive', { params }),
  toggleSkip: (id) => api.post(`/checkins/${id}/toggle-skip`),
  syncFromMio: (body) => api.post('/checkins/sync-from-mio', body),
  updateFlightCode: (id, flight_code) => api.patch(`/checkins/${id}/flight-code`, { flight_code }),
  getLogs: (params) => api.get('/checkins/logs', { params }),
  enrich: () => api.post('/checkins/enrich'),
};

// Templates
export const templateAPI = {
  downloadPassenger: () => api.get('/templates/passenger-import', { responseType: 'blob' }),
  downloadHotelRates: () => api.get('/templates/hotel-rates-import', { responseType: 'blob' }),
  downloadUser: () => api.get('/templates/user-import', { responseType: 'blob' }),
  downloadCompany: () => api.get('/templates/company-import', { responseType: 'blob' }),
};

// Users
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { newPassword }),
  resetMfa: (id) => api.post(`/users/${id}/reset-mfa`),
  requireMfa: (id, required = true) => api.post(`/users/${id}/require-mfa`, { required }),
  getPermissions: (id) => api.get(`/users/${id}/permissions`),
  updatePermissions: (id, permissions) => api.put(`/users/${id}/permissions`, { permissions }),
  bulkImport: (users) => api.post('/users/bulk-import', { users }),
  getDepartmentPresets: () => api.get('/users/department-presets'),
  createDepartmentPreset: (data) => api.post('/users/department-presets', data),
  updateDepartmentPreset: (id, data) => api.put(`/users/department-presets/${id}`, data),
  deleteDepartmentPreset: (id) => api.delete(`/users/department-presets/${id}`),
  getRequests: (status) => api.get('/users/requests', { params: { status } }),
  getRequestCount: () => api.get('/users/requests/count'),
  approveRequest: (id, data) => api.put(`/users/requests/${id}/approve`, data),
  rejectRequest: (id, data) => api.put(`/users/requests/${id}/reject`, data),
  getLoginLogs: (params) => api.get('/users/login-logs', { params }),
};

// Visas
export const visaAPI = {
  getAll: (params) => api.get('/visas', { params }),
  getStats: () => api.get('/visas/stats'),
  create: (data) => api.post('/visas', data),
  update: (id, data) => api.put(`/visas/${id}`, data),
  delete: (id) => api.delete(`/visas/${id}`),
  bulk: (rows) => api.post('/visas/bulk', { rows }),
  importPreview: (rows) => api.post('/visas/import-preview', { rows }),
  importBulk: (rows) => api.post('/visas/import-bulk', { rows }),
  getTrisSecenekleri: () => api.get('/visas/tris-secenekleri'),
};

// Visa Requirements (Vize Evrak Bilgileri)
export const visaRequirementsAPI = {
  getAll: () => api.get('/visa-requirements'),
  getByCode: (code) => api.get(`/visa-requirements/${code}`),
  update: (code, data) => api.put(`/visa-requirements/${code}`, data),
  scrape: () => api.post('/visa-requirements/scrape'),
};

export const visaAppointmentsAPI = {
  getAll: (params) => api.get('/visa-appointments', { params }),
  create: (data) => api.post('/visa-appointments', data),
  remove: (id) => api.delete(`/visa-appointments/${id}`),
  getQueue: (params) => api.get('/visa-appointments/queue', { params }),
  addToQueue: (data) => api.post('/visa-appointments/queue', data),
  updateQueue: (id, data) => api.put(`/visa-appointments/queue/${id}`, data),
  removeFromQueue: (id) => api.delete(`/visa-appointments/queue/${id}`),
  getStats: () => api.get('/visa-appointments/stats'),
};

export const mioSyncAPI = {
  getStatus: () => api.get('/sync-status'),
  getRecords: (slug, params) => api.get(`/sync-records/${slug}`, { params }),
  getErrors: () => api.get('/sync-errors'),
  clearErrors: () => api.delete('/sync-errors'),
  getRaporVeri: (viewSlug, params) => api.get(`/rapor-veri/${viewSlug}`, { params }),
  clearRaporVeri: (viewSlug) => api.delete(`/rapor-veri/${viewSlug}`),
  sendCommand: (type, payload) => api.post('/commands', { type, payload }),
  getCommand: (id) => api.get(`/commands/${id}`),
  getRaporSablonlar: () => api.get('/rapor-sablonlar'),
  getRaporSablonColumns: (id) => api.get(`/rapor-sablonlar/${id}/columns`),
  getDbSize: () => api.get('/mio-sync/db-size'),
  vacuum: () => api.post('/mio-sync/vacuum'),
  getUpdateFiles: () => api.get('/updates/list'),
  deleteUpdateFile: (filename) => api.delete(`/updates/${encodeURIComponent(filename)}`),
  // Endpoint Registry (admin dashboard)
  getEndpointRegistry: () => api.get('/endpoint-registry'),
  // V71.3 — MioDataSync uçları aç/kapat
  setDataSyncDurum: (aktif) => api.put('/mio-sync/datasync-durum', { aktif }),
};

// HR (İnsan Kaynakları)
export const hrAPI = {
  // Performance
  getPerformance: (params) => api.get('/hr', { params }),
  importPerformance: (data) => api.post('/hr/performance/import', data),
  importPerformanceMio: (data) => api.post('/hr/performance/import-mio', data),
  getHrBackups: () => api.get('/hr/performance/backups'),
  restoreHrBackup: (data) => api.post('/hr/performance/restore-from-backup', data),
  getPerformanceStats: (params) => api.get('/hr/performance/stats', { params }),
  getChartData: (params) => api.get('/hr/performance/chart-data', { params }),
  getPerformanceTrend: (params) => api.get('/hr/performance/chart-data', { params }),
  // Bonus
  calculateBonus: (data) => api.post('/hr/bonus/calculate', data),
  getBonusList: (params) => api.get('/hr/bonus', { params }),
  getBonus: (params) => api.get('/hr/bonus', { params }),
  getBonusConfig: () => api.get('/hr/bonus/config'),
  updateBonusConfig: (data) => api.put('/hr/bonus/config', data),
  resetBonusConfigDefaults: () => api.post('/hr/bonus/config/reset-defaults'),
  getBonusInsights: (params) => api.get('/hr/bonus/insights', { params }),
  getBonusTemplates: () => api.get('/hr/bonus/templates'),
  saveBonusTemplate: (data) => api.post('/hr/bonus/templates', data),
  deleteBonusTemplate: (id) => api.delete(`/hr/bonus/templates/${id}`),
  deletePerformanceStaff: (staffId, params) => api.delete(`/hr/performance/staff/${staffId}`, { params }),
  // Staff
  getStaff: (params) => api.get('/hr/staff', { params }),
  // V70.168 — Çalışanlar listesi: hassas alan (maaş/TC/IBAN) TAŞIMAZ, kendi filtreleriyle gelir
  getEmployees: (params) => api.get('/hr/employees', { params }),
  // V70.178 — uçak bileti hakları (yurt içi / yurt dışı)
  getTicketTypes: (all) => api.get('/hr/ticket-types', { params: all ? { all: 1 } : {} }),
  // V70.179 — kıdem kuralları + efektif limit durumu
  getTicketRules: () => api.get('/hr/ticket-rules'),
  createTicketRule: (data) => api.post('/hr/ticket-rules', data),
  updateTicketRule: (id, data) => api.put(`/hr/ticket-rules/${id}`, data),
  deleteTicketRule: (id) => api.delete(`/hr/ticket-rules/${id}`),
  recalcTicketRules: (year) => api.post('/hr/ticket-rules/recalc', { year }),
  getBenefitLimitDurum: (staffId) => api.get('/hr/benefits/limit-durum', { params: staffId ? { staff_id: staffId } : {} }),
  closeAdvance: (id, data) => api.post(`/hr/advances/${id}/close`, data || {}),
  reopenAdvance: (id) => api.post(`/hr/advances/${id}/reopen`),
  updateTicketType: (id, data) => api.put(`/hr/ticket-types/${id}`, data),
  getTicketBalance: (staffId, year) => api.get(`/hr/tickets/balance/${staffId}`, { params: year ? { year } : {} }),
  updateTicketBalance: (id, data) => api.put(`/hr/tickets/balance/${id}`, data),
  createTicketUsage: (data) => api.post('/hr/tickets/usages', data),
  deleteTicketUsage: (id) => api.delete(`/hr/tickets/usages/${id}`),
  selfTickets: (year) => api.get('/hr/self/tickets', { params: year ? { year } : {} }),
  // V70.169 — personel silme (bordro kalemi varsa sunucu reddeder) + özlük Excel akışı
  // V70.170 — numarasi olmayan personellere toplu personel no ver
  assignStaffNumbers: () => api.post('/hr/employees/assign-numbers'),
  deleteStaffCheck: (id) => api.get(`/hr/staff/${id}/delete-check`),
  deleteStaffHard: (id, onay_ad) => api.delete(`/hr/staff/${id}`, { data: { onay_ad } }),
  exportOzluk: (durum) => api.get('/hr/employees/export', { params: durum ? { durum } : {}, responseType: 'blob' }),
  importOzluk: (file, apply) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('apply', apply ? '1' : '0');
    return api.post('/hr/employees/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // V70.163 — effective-dated org atamalari (spec §1.3: gecmis ezilmez)
  getAssignments: (staffId) => api.get(`/hr/staff/${staffId}/assignments`),
  // V70.167 — İK politika ayarları (günlük çalışma saati vb.)
  getHrSettings: () => api.get('/hr/settings'),
  updateHrSetting: (anahtar, deger) => api.put(`/hr/settings/${anahtar}`, { deger }),
  // V70.166 — izin Excel'i içe aktarma (apply=0 kuru çalıştırma, apply=1 yazar)
  importLeaves: (file, { year, defaultTip, apply }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('year', year);
    fd.append('default_tip', defaultTip);
    fd.append('apply', apply ? '1' : '0');
    return api.post('/hr/leaves/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // V70.165 — işten çıkış (offboarding)
  offboardingCheck: (staffId, date) => api.get(`/hr/staff/${staffId}/offboarding-check`, { params: date ? { date } : {} }),
  offboardStaff: (staffId, data) => api.post(`/hr/staff/${staffId}/offboard`, data),
  reactivateStaff: (staffId) => api.post(`/hr/staff/${staffId}/reactivate`),
  createAssignment: (staffId, data) => api.post(`/hr/staff/${staffId}/assignments`, data),
  deleteAssignment: (staffId, aid) => api.delete(`/hr/staff/${staffId}/assignments/${aid}`),
  getTitles: () => api.get('/hr/titles'),
  createTitle: (data) => api.post('/hr/titles', data),
  getLocations: () => api.get('/hr/locations'),
  createLocation: (data) => api.post('/hr/locations', data),
  createStaff: (data) => api.post('/hr/staff', data),
  updateStaff: (id, data) => api.put(`/hr/staff/${id}`, data),
  deleteStaff: (id) => api.delete(`/hr/staff/${id}`),
  importSalary: (data) => api.post('/hr/staff/import-salary', data),
  getBirthdaysToday: () => api.get('/hr/staff/birthdays-today'),
  getMyBirthdayToday: () => api.get('/hr/staff/my-birthday-today'),
  // Upload Tracker
  getUploadTracker: (params) => api.get('/hr/upload-tracker', { params }),
  getUploadProgress: (params) => api.get('/hr/upload-tracker/progress', { params }),
  markUpload: (data) => api.post('/hr/upload-tracker/mark', data),
  // TEMP: test seed & cleanup
  testSeed: () => api.post('/hr/test/seed'),
  testCleanup: () => api.delete('/hr/test/cleanup'),

  // ── V50 Sprint 1: Özlük + Belge + İzin + Org + Audit ──
  getStaffFull: (id) => api.get(`/hr/staff/${id}/full`),
  updateStaffFull: (id, data) => api.put(`/hr/staff/${id}/full`, data),
  uploadStaffDocument: (id, formData) => api.post(`/hr/staff/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadStaffDocument: (id, docId) => `/api/hr/staff/${id}/documents/${docId}/download`,
  deleteStaffDocument: (id, docId) => api.delete(`/hr/staff/${id}/documents/${docId}`),
  uploadStaffPhoto: (id, formData) => api.post(`/hr/staff/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  staffPhotoUrl: (id) => `/api/hr/staff/${id}/photo`,

  getLeaveTypes: (params) => api.get('/hr/leave-types', { params }),
  updateLeaveType: (id, data) => api.put(`/hr/leave-types/${id}`, data),
  getLeaveBalance: (staffId, year) => api.get(`/hr/leaves/balance/${staffId}`, { params: year ? { year } : {} }),
  createLeaveRequest: (formData) => api.post('/hr/leaves/request', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getLeaveRequests: (params) => api.get('/hr/leaves/requests', { params }),
  approveLeave: (id, comment) => api.post(`/hr/leaves/requests/${id}/approve`, { comment }),
  rejectLeave: (id, comment) => api.post(`/hr/leaves/requests/${id}/reject`, { comment }),
  cancelLeave: (id, comment) => api.post(`/hr/leaves/requests/${id}/cancel`, { comment }),
  getLeaveCalendar: (start, end) => api.get('/hr/leaves/calendar', { params: { start, end } }),
  leaveDocumentUrl: (id) => `/api/hr/leaves/requests/${id}/document`,

  // V70.53 — tanımlar & sayaçlar
  createLeaveType: (data) => api.post('/hr/leave-types', data),
  getLeaveBalancesOverview: (year) => api.get('/hr/leaves/balances-overview', { params: year ? { year } : {} }),
  updateLeaveBalance: (id, data) => api.put(`/hr/leaves/balances/${id}`, data),
  getBenefitLimits: () => api.get('/hr/benefits/limits'),
  updateBenefitLimit: (kind, data) => api.put(`/hr/benefits/limits/${kind}`, data),
  getBenefitSummary: (params) => api.get('/hr/benefits/summary', { params }),
  getPendingApprovals: () => api.get('/hr/approvals/pending'), // V70.59 — onaycının bekleyen izin+avans+harcama talepleri

  // V70.64 — departman + pozisyon tanım/hiyerarşi
  getHrDepartments: (params) => api.get('/hr/definitions/departments', { params }),
  createHrDepartment: (data) => api.post('/hr/definitions/departments', data),
  updateHrDepartment: (id, data) => api.put(`/hr/definitions/departments/${id}`, data),
  getHrPositions: (params) => api.get('/hr/definitions/positions', { params }),
  createHrPosition: (data) => api.post('/hr/definitions/positions', data),
  updateHrPosition: (id, data) => api.put(`/hr/definitions/positions/${id}`, data),

  getHolidays: (year) => api.get('/hr/holidays', { params: year ? { year } : {} }),
  createHoliday: (data) => api.post('/hr/holidays', data),
  deleteHoliday: (id) => api.delete(`/hr/holidays/${id}`),

  getOrgChart: () => api.get('/hr/org-chart'),
  updateManager: (id, manager_id) => api.put(`/hr/staff/${id}/manager`, { manager_id }),

  getAuditLog: (params) => api.get('/hr/audit-log', { params }),

  // V70.179 — Site/Route Registry (spec v1.1 Faz 0)
  getSiteRegistry: () => api.get('/hr/registry'),
  getSiteRegistryAudit: () => api.get('/hr/registry/audit'),
  getEffectiveAccess: (params) => api.get('/hr/registry/effective', { params }),

  // V70.188 — Faz 1 §7/§8: yetki motoru uçları
  getPermissionCatalog: () => api.get('/hr/permissions/catalog'),
  getEffectivePermissions: (params) => api.get('/hr/permissions/effective', { params }),
  checkPermissionFor: (params) => api.get('/hr/permissions/check', { params }),
  getAuthAudit: (params) => api.get('/hr/permissions/audit', { params }),

  // V70.194 — Faz 2 §3.2/§3.3: Kullanıcı & Yetki Yönetimi
  getAccessEditorTree: () => api.get('/hr/access/editor-tree'),
  getAccessUsers: () => api.get('/hr/access/users'),
  getAccessUser: (id) => api.get(`/hr/access/users/${id}`),
  getAccessRoles: () => api.get('/hr/access/roles'),
  getAccessRole: (id) => api.get(`/hr/access/roles/${id}`),
  createAccessRole: (data) => api.post('/hr/access/roles', data),
  updateAccessRole: (id, data) => api.put(`/hr/access/roles/${id}`, data),
  setAccessRolePermission: (id, data) => api.put(`/hr/access/roles/${id}/permissions`, data),
  getAccessPermissionSets: () => api.get('/hr/access/permission-sets'),
  assignAccessRole: (userId, data) => api.post(`/hr/access/users/${userId}/roles`, data),
  revokeAccessRole: (userId, assignmentId) => api.delete(`/hr/access/users/${userId}/roles/${assignmentId}`),
  assignAccessSet: (userId, data) => api.post(`/hr/access/users/${userId}/permission-sets`, data),
  revokeAccessSet: (userId, assignmentId) => api.delete(`/hr/access/users/${userId}/permission-sets/${assignmentId}`),
  setAccessOverride: (userId, data) => api.post(`/hr/access/users/${userId}/overrides`, data),
  revokeAccessOverride: (userId, overrideId) => api.delete(`/hr/access/users/${userId}/overrides/${overrideId}`),
  getAccessDelegations: () => api.get('/hr/access/delegations'),
  createAccessDelegation: (data) => api.post('/hr/access/delegations', data),
  revokeAccessDelegation: (id, data) => api.post(`/hr/access/delegations/${id}/revoke`, data),
  simulateAccessUser: (id) => api.get(`/hr/access/simulate/${id}`),
  getAccessReview: () => api.get('/hr/access/review'),

  // Sprint 2 — Vardiya / Puantaj / Mesai / Uyum (V50.2)
  getShiftPatterns: () => api.get('/hr/shift-patterns'),
  createShiftPattern: (data) => api.post('/hr/shift-patterns', data),
  updateShiftPattern: (id, data) => api.put(`/hr/shift-patterns/${id}`, data),
  deleteShiftPattern: (id) => api.delete(`/hr/shift-patterns/${id}`),
  getShiftAssignments: (params) => api.get('/hr/shift-assignments', { params }),
  bulkAssignShifts: (data) => api.post('/hr/shift-assignments/bulk', data),
  deleteShiftAssignment: (id) => api.delete(`/hr/shift-assignments/${id}`),

  getTimesheet: (params) => api.get('/hr/timesheet', { params }),
  createTimesheet: (data) => api.post('/hr/timesheet', data),
  updateTimesheet: (id, data) => api.put(`/hr/timesheet/${id}`, data),
  deleteTimesheet: (id) => api.delete(`/hr/timesheet/${id}`),
  timesheetCheckin: () => api.post('/hr/timesheet/checkin'),
  timesheetCheckout: () => api.post('/hr/timesheet/checkout'),
  approveTimesheet: (id) => api.post(`/hr/timesheet/${id}/approve`),
  rejectTimesheet: (id, comment) => api.post(`/hr/timesheet/${id}/reject`, { comment }),
  bulkImportTimesheet: (rows) => api.post('/hr/timesheet/bulk-import', { rows }),
  getRemoteWork: (year) => api.get('/hr/remote-work', { params: { year } }),
  setRemoteWork: (data) => api.put('/hr/remote-work', data),
  setRemoteWorkAyarlar: (data) => api.put('/hr/remote-work/ayarlar', data),

  getOvertimeRequests: (params) => api.get('/hr/overtime-requests', { params }),
  createOvertimeRequest: (data) => api.post('/hr/overtime-requests', data),
  approveOvertime: (id, comment) => api.post(`/hr/overtime-requests/${id}/approve`, { comment }),
  rejectOvertime: (id, comment) => api.post(`/hr/overtime-requests/${id}/reject`, { comment }),

  checkCompliance: (params) => api.get('/hr/compliance/check', { params }),
  getComplianceAlerts: (params) => api.get('/hr/compliance/alerts', { params }),
  runComplianceAll: (data) => api.post('/hr/compliance/run-all', data),

  // Sprint 3 — Maaş + Bordro
  getSalaries: (params) => api.get('/hr/salaries', { params }),
  createSalary: (data) => api.post('/hr/salaries', data),
  updateSalary: (id, data) => api.put(`/hr/salaries/${id}`, data),
  deleteSalary: (id) => api.delete(`/hr/salaries/${id}`),
  getKesintiTanim: () => api.get('/hr/payroll/kesinti-tanim'),
  createKesintiTanim: (data) => api.post('/hr/payroll/kesinti-tanim', data),
  deleteKesintiTanim: (id) => api.delete(`/hr/payroll/kesinti-tanim/${id}`),
  getPayrollRuns: () => api.get('/hr/payroll/runs'),
  getPayrollRunItems: (id) => api.get(`/hr/payroll/runs/${id}/items`),
  runPayroll: (data) => api.post('/hr/payroll/run', data),
  lockPayrollRun: (id) => api.post(`/hr/payroll/runs/${id}/lock`),
  deletePayrollRun: (id) => api.delete(`/hr/payroll/runs/${id}`),
  payrollLucaExport: (id) => api.get(`/hr/payroll/runs/${id}/luca-export`, { responseType: 'blob' }),
  payrollBordroPdf: (id, staffId) => api.get(`/hr/payroll/runs/${id}/bordro-pdf/${staffId}`, { responseType: 'blob' }),

  // Sprint 4 — Avans + Harcama + OCR
  getAdvances: (params) => api.get('/hr/advances', { params }),
  createAdvance: (data) => api.post('/hr/advances', data),
  approveAdvance: (id) => api.post(`/hr/advances/${id}/approve`),
  rejectAdvance: (id, comment) => api.post(`/hr/advances/${id}/reject`, { comment }),
  payAdvance: (id, odenme_tarihi) => api.post(`/hr/advances/${id}/pay`, { odenme_tarihi }),
  deleteAdvance: (id) => api.delete(`/hr/advances/${id}`),
  getExpenseCategories: () => api.get('/hr/expense-categories'),
  createExpenseCategory: (ad) => api.post('/hr/expense-categories', { ad }),
  deleteExpenseCategory: (id) => api.delete(`/hr/expense-categories/${id}`),
  getExpenses: (params) => api.get('/hr/expenses', { params }),
  createExpense: (formData) => api.post('/hr/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  approveExpense: (id) => api.post(`/hr/expenses/${id}/approve`),
  rejectExpense: (id, comment) => api.post(`/hr/expenses/${id}/reject`, { comment }),
  deleteExpense: (id) => api.delete(`/hr/expenses/${id}`),
  expenseFisUrl: (id) => `${API_BASE}/hr/expenses/${id}/fis`,
  expenseOcr: (id) => api.post(`/hr/expenses/${id}/ocr`),
  // Sprint 5 — Kariyer
  getCareerHistory: (staff_id) => api.get('/hr/career-history', { params: staff_id ? { staff_id } : {} }),
  createCareerHistory: (data) => api.post('/hr/career-history', data),
  deleteCareerHistory: (id) => api.delete(`/hr/career-history/${id}`),
  // Sprint 5 — Eğitim
  getTrainings: () => api.get('/hr/trainings'),
  createTraining: (data) => api.post('/hr/trainings', data),
  updateTraining: (id, data) => api.put(`/hr/trainings/${id}`, data),
  deleteTraining: (id) => api.delete(`/hr/trainings/${id}`),
  getTrainingAttendees: (id) => api.get(`/hr/trainings/${id}/attendees`),
  addTrainingAttendees: (id, staff_ids) => api.post(`/hr/trainings/${id}/attendees`, { staff_ids }),
  updateTrainingAttendee: (id, data) => api.put(`/hr/training-attendees/${id}`, data),
  deleteTrainingAttendee: (id) => api.delete(`/hr/training-attendees/${id}`),
  // Sprint 5 — Performans 360°
  getEvalCycles: () => api.get('/hr/eval-cycles'),
  // V71 — /users admin'e özel; İK yetkilisi için hafif aktif kullanıcı listeleri
  getEvalEvaluatorCandidates: () => api.get('/hr/eval-cycles/evaluator-candidates'),
  getStaffUserCandidates: () => api.get('/hr/staff/user-candidates'),
  createEvalCycle: (data) => api.post('/hr/eval-cycles', data),
  updateEvalCycle: (id, data) => api.put(`/hr/eval-cycles/${id}`, data),
  deleteEvalCycle: (id) => api.delete(`/hr/eval-cycles/${id}`),
  getEvalQuestions: (cycleId) => api.get(`/hr/eval-cycles/${cycleId}/questions`),
  createEvalQuestion: (cycleId, data) => api.post(`/hr/eval-cycles/${cycleId}/questions`, data),
  deleteEvalQuestion: (id) => api.delete(`/hr/eval-questions/${id}`),
  createEvalInvites: (cycleId, evaluator_user_ids, evaluated_staff_ids) => api.post(`/hr/eval-cycles/${cycleId}/invite`, { evaluator_user_ids, evaluated_staff_ids }),
  getEvalInvites: (cycleId) => api.get(`/hr/eval-cycles/${cycleId}/invites`),
  getEvalMyTasks: () => api.get('/hr/eval/my-tasks'),
  getEvalInvite: (id) => api.get(`/hr/eval/invite/${id}`),
  submitEvalInvite: (id, responses) => api.post(`/hr/eval/invite/${id}/submit`, { responses }),
  finalizeEvalCycle: (id) => api.post(`/hr/eval-cycles/${id}/finalize`),
  getEvalSummary: (cycleId) => api.get(`/hr/eval-cycles/${cycleId}/summary`),
  // Sprint 6 — Self Service
  selfDashboard: () => api.get('/hr/self/dashboard'),
  selfPayslips: () => api.get('/hr/self/payslips'),
  // V70.159 — kendi bordro PDF'i (staff_id URL'de YOK; sunucu oturumdan turetir)
  selfPayslipPdf: (runId) => api.get(`/hr/self/payslips/${runId}/pdf`, { responseType: 'blob' }),
  publishPayrollRun: (id) => api.post(`/hr/payroll/runs/${id}/publish`),
  unpublishPayrollRun: (id) => api.post(`/hr/payroll/runs/${id}/unpublish`),
  selfProfile: (reveal) => api.get('/hr/self/profile', { params: reveal ? { reveal: 1 } : {} }),
  selfProfileUpdate: (data) => api.put('/hr/self/profile', data),
  selfPhotoUpload: (file) => { const fd = new FormData(); fd.append('photo', file); return api.post('/hr/self/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  selfTeam: () => api.get('/hr/self/team'),
  selfTrainings: () => api.get('/hr/self/trainings'),
};

// Content Engine
// V70.70 — Fikrim Var (öneri/hata bildirimleri)
export const ideasAPI = {
  send: (data) => api.post('/ideas', data),
  list: (params) => api.get('/ideas', { params }),
  setDurum: (id, durum) => api.patch(`/ideas/${id}`, { durum }),
  remove: (id) => api.delete(`/ideas/${id}`),
};

// V71.16 — Operasyon Haritası (katman başına mevcut yetki)
export const opsMapAPI = {
  get: () => api.get('/ops-map'),
};

export const itineraLeadsAPI = {
  list: (params) => api.get('/itinera-leads', { params }),
  get: (id) => api.get(`/itinera-leads/${encodeURIComponent(id)}`),
  update: (id, data) => api.patch(`/itinera-leads/${encodeURIComponent(id)}`, data),
  users: () => api.get('/itinera-leads/users'),
  count: () => api.get('/itinera-leads/count'), // V71.51 — sidebar rozeti
  seen: () => api.post('/itinera-leads/seen'), // V71.50.3 — sayfa açılınca rozet sıfırlanır
};

// V71.8 — Santral Yönetimi (kendi Asterisk santralimiz)
export const santralAPI = {
  get: () => api.get('/santral'),
  saveStaff: (id, data) => api.put(`/santral/staff/${id}`, data),
  saveQueue: (id, data) => api.put(`/santral/queues/${id}`, data),
  saveSettings: (data) => api.put('/santral/settings', data),
  messages: () => api.get('/santral/messages'),
  audio: (id) => api.get(`/santral/messages/${id}/audio`, { responseType: 'blob' }),
  log: () => api.get('/santral/log'),
  cagrilar: (params) => api.get('/santral/cagrilar', { params }),
  cagri: (id) => api.get(`/santral/cagrilar/${encodeURIComponent(id)}`),
  cagriKayit: (id) => api.get(`/santral/cagrilar/${encodeURIComponent(id)}/kayit`, { responseType: 'blob' }),
  cagriOzet: (id) => api.post(`/santral/cagrilar/${encodeURIComponent(id)}/ozet`),
  rapor: (gun) => api.get('/santral/rapor', { params: { gun } }),
  sesler: () => api.get('/santral/sesler'),
  ses: (ad) => api.get(`/santral/sesler/${encodeURIComponent(ad)}`, { responseType: 'blob' }),
  canli: () => api.get('/santral/canli'), // V71.36 — arayan ekranı (her personel)
};

// V71.9 — Rehberim (santral arayan adı)
export const rehberAPI = {
  list: (params) => api.get('/santral-rehber', { params }),
  numaralar: () => api.get('/santral-rehber/numaralar'),
  add: (kisiler) => api.post('/santral-rehber', { kisiler }),
  update: (id, data) => api.patch(`/santral-rehber/${id}`, data),
  remove: (ids) => api.post('/santral-rehber/sil', { ids }),
};

// Dashboard
export const dashboardAPI = {
  getMyLayout: () => api.get('/dashboard/my-layout'),
  getWidgets: () => api.get('/dashboard/widgets'),
  getLayouts: () => api.get('/dashboard/layouts'),
  saveLayout: (presetId, widgetConfig) => api.put(`/dashboard/layouts/${presetId}`, { widget_config: widgetConfig }),
  resetLayout: (presetId) => api.delete(`/dashboard/layouts/${presetId}`),
  getTcmbRates: () => api.get('/dashboard/tcmb-rates'),
  getGfRates: () => api.get('/dashboard/gf-rates'),
  getWorldWeather: () => api.get('/dashboard/world-weather'),
  getMySettings: () => api.get('/dashboard/my-settings'),
  saveMySettings: (data) => api.put('/dashboard/my-settings', data),
};

// Activity Log
export const activityLogAPI = {
  getRecent: (params) => api.get('/activity-log/recent', { params }),
  getAll: (params) => api.get('/activity-log', { params }),
  getFilters: () => api.get('/activity-log/filters'),
};

// OTP Feed (banka şifre akışı)
export const otpFeedAPI = {
  getAll: (params) => api.get('/otp-feed', { params }),
  getStats: () => api.get('/otp-feed/stats'),
  getWidget: (params) => api.get('/otp-feed/widget', { params }),
  update: (id, payload) => api.patch(`/otp-feed/${id}`, payload),
  getTone: () => api.get('/otp-feed/tone'),
  setTone: (data) => api.put('/otp-feed/tone', data),
};

// Reservations
export const reservationAPI = {
  getMioVoucherPdfUrl: (type, id) => `${API_BASE}/reservations/mio-voucher/${type}/${id}/pdf`,
  getCancellationRules: () => api.get('/reservations/mio-voucher/cancellation-rules'),
  seedCancellationRules: () => api.post('/reservations/mio-voucher/cancellation-rules/seed'),
  createCancellationRule: (data) => api.post('/reservations/mio-voucher/cancellation-rules', data),
  updateCancellationRule: (id, data) => api.put(`/reservations/mio-voucher/cancellation-rules/${id}`, data),
  deleteCancellationRule: (id) => api.delete(`/reservations/mio-voucher/cancellation-rules/${id}`),
  createClause: (data) => api.post('/reservations/mio-voucher/clauses', data),
  updateClause: (id, data) => api.put(`/reservations/mio-voucher/clauses/${id}`, data),
  deleteClause: (id) => api.delete(`/reservations/mio-voucher/clauses/${id}`),
  parseRentalHtml: (html) => api.post('/reservations/mio-voucher/parse-rental-html', { html }),
  saveRentalExtra: (id, yolcu360_data) => api.post(`/reservations/mio-voucher/save-rental-extra/${id}`, { yolcu360_data }),
  // V70.160 — Hub rezervasyonları (MIO down fallback)
};

// Notifications
export const notificationAPI = {
  getSettings: () => api.get('/notifications/settings'),
  updateSetting: (id, data) => api.put(`/notifications/settings/${id}`, data),
  // V71.14 — kalıcı bildirimler
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  remove: (id) => api.delete(`/notifications/${id}`),
};

// V71.14 — Görevler
export const taskAPI = {
  summary: () => api.get('/tasks/summary'),
  list: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  remove: (id) => api.delete(`/tasks/${id}`),
  users: () => api.get('/tasks/users'),
  systemHistory: () => api.get('/tasks/system-history'),
};

// Flights (HangiUcak proxy)
// V71.23 — botun açtığı uçuş rezervasyonları
export const ucusRezervasyonAPI = {
  liste: () => api.get('/ucus-rezervasyonlar'),
  getir: (id) => api.get(`/ucus-rezervasyonlar/${id}`),
  yenile: (id) => api.post(`/ucus-rezervasyonlar/${id}/yenile`),
  iptal: (id) => api.post(`/ucus-rezervasyonlar/${id}/iptal`),
};

export const flightAPI = {
  search: (params) => api.post('/flights/search', params),
  searchLogStats: (days) => api.get('/flights/search-log/stats', { params: days ? { days } : {} }),
  results: (params) => api.post('/flights/results', params),
  airports: (q) => api.get('/flights/airports', { params: { q } }),
  sendAlternatives: (data) => api.post('/flights/send-alternatives', data),
  combos: (data) => api.post('/flights/combos', data),
  combosOnArama: (data) => api.post('/flights/combos/on-arama', data),
  combosOlcum: (gun) => api.get('/flights/combos/olcum', { params: gun ? { gun } : {} }),
  health: () => api.get('/flights/health'),
  thyPrice: (offerId) => api.post('/flights/thy-price', { offerId }),
  thyStatus: () => api.get('/flights/thy-status'),
  priceTrend: (params) => api.get('/flights/price-trend', { params }),
  priceHeatmap: (params) => api.get('/flights/price-heatmap', { params }),
  popularRoutes: () => api.get('/flights/popular-routes'),
  airlines: (provider) => api.get('/flights/airlines', { params: provider ? { provider } : {} }),
  airlinesSync: () => api.post('/flights/airlines/sync'),
  locations: (params) => api.get('/flights/locations', { params: params || {} }),
  locationsSync: () => api.post('/flights/locations/sync'),
  locationsBackfill: () => api.post('/flights/locations/backfill'),
  debugProviders: (params) => api.get('/flights/debug-providers', { params: params || {} }),
  brandedFares: (keys) => api.post('/flights/branded-fares', { keys }),
  fxRates: () => api.get('/flights/exchange-rates'),
  aeSync: () => api.post('/flights/locations/ae-sync'),
  aeStatus: () => api.get('/flights/locations/ae-status'),
  aeRoutesSync: (airports) => api.post('/flights/locations/ae-routes-sync', airports ? { airports } : {}),
  routesFrom: (iata) => api.get(`/flights/routes/from/${iata}`),
  routesTo: (iata) => api.get(`/flights/routes/to/${iata}`),
  aeList: (params) => api.get('/flights/locations/ae-list', { params: params || {} }),
  aeGet: (type, iata) => api.get(`/flights/locations/ae/${type}/${iata}`),
  aeUpdate: (type, iata, body) => api.patch(`/flights/locations/ae/${type}/${iata}`, body),
  aeReset: (type, iata) => api.post(`/flights/locations/ae/${type}/${iata}/reset`),
  aeToggleActive: (type, iata, active) => api.post(`/flights/locations/ae/${type}/${iata}/toggle-active`, active === undefined ? {} : { active }),
};

// V70.97 — Otel Arama (TravelRobot Hotel)
export const hotelSearchAPI = {
  health: () => api.get('/hotels/health'),
  destinations: (q) => api.get('/hotels/destinations', { params: { q } }),
  destinationsGeo: (q) => api.get('/hotels/destinations/geo', { params: { q } }),
  destinationsSync: (countries) => api.post('/hotels/destinations/sync', countries ? { countries } : {}),
  destinationsStats: () => api.get('/hotels/destinations/stats'), // V70.127 — ülke bazlı dağılım (Sistem sayfası)
  probe: () => api.post('/hotels/destinations/probe', {}),
  geocode: (q) => api.get('/hotels/geocode', { params: { q } }),
  routeDistance: (from, to) => api.post('/hotels/route-distance', { from, to }),
  mapsKey: () => api.get('/hotels/maps-key'),
  search: (params) => api.post('/hotels/search', params),
  rooms: (hotelCode, searchKey) => api.post('/hotels/rooms', { hotelCode, searchKey }),
  roomsBatch: (params) => api.post('/hotels/rooms-batch', params), // V70.125 — toplu oda prefetch
  cancelPolicies: (params) => api.post('/hotels/cancel-policies', params), // V70.142 — iptal politikaları (sonuç kartı + sepet)
  details: (hotelCode) => api.get(`/hotels/details/${hotelCode}`),
  sendOffers: (data) => api.post('/hotels/send-offers', data),
};

// V70.140 — Arama Logları (admin): uçuş+otel arama kayıtları ve sonuç fiyatları
export const searchLogsAPI = {
  summary: (days) => api.get('/search-logs/summary', { params: days ? { days } : {} }),
  flights: (params) => api.get('/search-logs/flights', { params }),
  flightPrices: (id) => api.get(`/search-logs/flights/${id}/prices`),
  hotels: (params) => api.get('/search-logs/hotels', { params }),
  hotelPrices: (id) => api.get(`/search-logs/hotels/${id}/prices`),
  users: () => api.get('/search-logs/users'),
  exportCsv: (params) => api.get('/search-logs/export', { params, responseType: 'blob' }),
  analyticsFlights: (params) => api.get('/search-logs/analytics/flights', { params }), // V70.147
  analyticsHotels: (params) => api.get('/search-logs/analytics/hotels', { params }), // V70.147
};

// Portal Management (Admin → Client Portal)
export const portalManagementAPI = {
  getCompanies: () => api.get('/portal-management/companies'),
  createCompany: (data) => api.post('/portal-management/companies', data),
  createUser: (data) => api.post('/portal-management/users', data),
  importPreview: (formData) => api.post('/portal-management/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importCommit: (data) => api.post('/portal-management/import/commit', data),
  getStats: () => api.get('/portal-management/stats'),
  getImports: (params) => api.get('/portal-management/imports', { params }),
  deleteData: (data) => api.delete('/portal-management/data', { data }),
};

export const mailingAPI = {
  getCampaigns: (params) => api.get('/mailing/campaigns', { params }),
  getCampaign: (id) => api.get(`/mailing/campaigns/${id}`),
  createCampaign: (data) => api.post('/mailing/campaigns', data),
  updateCampaign: (id, data) => api.put(`/mailing/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/mailing/campaigns/${id}`),
  duplicateCampaign: (id) => api.post(`/mailing/campaigns/${id}/duplicate`),
  getRecipients: (id, params) => api.get(`/mailing/campaigns/${id}/recipients`, { params }),
  importRecipients: (id, data) => api.post(`/mailing/campaigns/${id}/recipients/import`, data),
  updateRecipient: (cId, rId, data) => api.put(`/mailing/campaigns/${cId}/recipients/${rId}`, data),
  bulkSelect: (id, data) => api.post(`/mailing/campaigns/${id}/recipients/bulk-select`, data),
  bulkGender: (id, data) => api.post(`/mailing/campaigns/${id}/recipients/bulk-gender`, data),
  deleteRecipient: (cId, rId) => api.delete(`/mailing/campaigns/${cId}/recipients/${rId}`),
  deleteRecipients: (id) => api.delete(`/mailing/campaigns/${id}/recipients`),
  sendCampaign: (id) => api.post(`/mailing/campaigns/${id}/send`),
  preview: (id, params) => api.get(`/mailing/campaigns/${id}/preview`, { params }),
  getTracking: (id) => api.get(`/mailing/campaigns/${id}/tracking`),
};

// Weekly Duty (Haftanın Nöbetçisi)
export const weeklyDutyAPI = {
  getCurrent: () => api.get('/weekly-duty/current'),
  getAll: () => api.get('/weekly-duty'),
  create: (data) => api.post('/weekly-duty', data),
  update: (id, data) => api.put(`/weekly-duty/${id}`, data),
  delete: (id) => api.delete(`/weekly-duty/${id}`),
};

// Announcements (Duyurular)
export const announcementAPI = {
  getActive: () => api.get('/announcements/active'),
  getAll: () => api.get('/announcements'),
  create: (data) => api.post('/announcements', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  delete: (id) => api.delete(`/announcements/${id}`),
};

// V70.171 — Bilet Düzenleyici: THY e-bileti → MTU markalı belge (kayıt tutulmaz)
export const ticketEditorAPI = {
  parseFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/ticket-editor/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  parseText: (text) => api.post('/ticket-editor/parse', { text }),
  render: (ticket) => api.post('/ticket-editor/render', { ticket }),
  pdf: (ticket) => api.post('/ticket-editor/pdf', { ticket }, { responseType: 'blob' }),
};

export const passportReaderAPI = {
  getAll: (params) => api.get('/passport-reader/passports', { params }),
  getById: (id) => api.get(`/passport-reader/passports/${id}`),
  delete: (id) => api.delete(`/passport-reader/passports/${id}`),
  // Auto-update
  getUpdateLogs: (params) => api.get('/passport-reader/update/logs', { params }),
  getUpdateInfo: () => api.get('/passport-reader/update/info'),
  publishUpdate: (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/passport-reader/update/publish', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
};

export const mailTrackingAPI = {
  getAll: (params) => api.get('/mail-tracking', { params }),
  getById: (id) => api.get(`/mail-tracking/${id}`),
  update: (id, data) => api.put(`/mail-tracking/${id}`, data),
  assign: (id, userId) => api.post(`/mail-tracking/${id}/assign`, { user_id: userId }),
  bulkAssign: (ids, userId) => api.post('/mail-tracking/bulk-assign', { thread_ids: ids, user_id: userId }),
  bulkStatus: (ids, status) => api.post('/mail-tracking/bulk-status', { thread_ids: ids, status }),
  statsOverview: (params) => api.get('/mail-tracking/stats/overview', { params }),
  statsStaff: (params) => api.get('/mail-tracking/stats/staff', { params }),
  statsDaily: (params) => api.get('/mail-tracking/stats/daily', { params }),
  statsSlaAlerts: () => api.get('/mail-tracking/stats/sla-alerts'),
  getSlaConfig: () => api.get('/mail-tracking/sla-config'),
  updateSlaConfig: (config) => api.put('/mail-tracking/sla-config', { config }),
  getDomains: (params) => api.get('/mail-tracking/domains', { params }),
  updateDomain: (id, data) => api.put(`/mail-tracking/domains/${id}`, data),
  createDomain: (data) => api.post('/mail-tracking/domains', data),
  deleteDomain: (id) => api.delete(`/mail-tracking/domains/${id}`),
  syncDomains: () => api.post('/mail-tracking/domains/sync'),
  cleanupDomains: () => api.post('/mail-tracking/domains/cleanup'),
  getDomainCompanies: () => api.get('/mail-tracking/domains/companies'),
  getLogs: (params) => api.get('/mail-tracking/logs', { params }),
  getUsers: () => api.get('/mail-tracking/users'),
  callbackNotify: (data) => api.post('/mail-tracking/callback/notify', data),
  recalcResponseTimes: () => api.post('/mail-tracking/recalc-response-times'),
  cloneToDept: (data) => api.post('/mail-tracking/clone-to-dept', data),
  getIgnoredEmails: () => api.get('/mail-tracking/ignored-emails'),
  addIgnoredEmail: (data) => api.post('/mail-tracking/ignored-emails', data),
  deleteIgnoredEmail: (id) => api.delete(`/mail-tracking/ignored-emails/${id}`),
  // V71.54 — M-Desk devri
  getDevirDurum: () => api.get('/mail-tracking/devir-durum'),
  setDevirDurum: (durum) => api.put('/mail-tracking/devir-durum', { durum }),
  exportMdesk: () => api.get('/mail-tracking/export-mdesk'),
  mdeskKarsilastir: (gun) => api.get('/mail-tracking/mdesk-karsilastir', { params: gun ? { gun } : {} }),
};

export const travelrobotAPI = {
  sync: (data) => api.post('/travelrobot/sync', data),
  getReports: (params) => api.get('/travelrobot/reports', { params }),
  getStatusSummary: (params) => api.get('/travelrobot/reports/status-summary', { params }), // V70.129 — durum dağılımı teşhisi
  getStats: (params) => api.get('/travelrobot/stats', { params }),
  getSyncLog: () => api.get('/travelrobot/sync-log'),
  getProductTypes: () => api.get('/travelrobot/product-types'),
  deleteReports: (params) => api.delete('/travelrobot/reports', { params }),
};

export const whatsappAPI = {
  status: () => api.get('/whatsapp/status'),
  send: (data) => api.post('/whatsapp/send', data),
  messages: (params) => api.get('/whatsapp/messages', { params }),
  // V71.11 — WhatsApp masası
  conversations: (params) => api.get('/whatsapp/conversations', { params }),
  unread: () => api.get('/whatsapp/unread'), // V71.14.8 — sidebar rozeti
  conversation: (id) => api.get(`/whatsapp/conversations/${id}`),
  openConversation: (data) => api.post('/whatsapp/conversations/open', data),
  sendTo: (id, data) => api.post(`/whatsapp/conversations/${id}/send`, data),
  updateConversation: (id, data) => api.patch(`/whatsapp/conversations/${id}`, data),
  templates: (params) => api.get('/whatsapp/templates', { params }),
  users: () => api.get('/whatsapp/users'),
  media: (id) => api.get(`/whatsapp/media/${id}`, { responseType: 'blob' }),
  ayarlar: () => api.get('/whatsapp/ayarlar'),
  ayarKaydet: (data) => api.put('/whatsapp/ayarlar', data),
  botOzet: (gun = 7) => api.get('/whatsapp/bot-ozet', { params: { gun } }), // V71.30 — asistan
  golgeOzet: (gun = 7) => api.get('/whatsapp/golge-ozet', { params: { gun } }), // V71.27 — gölge mod
  golgeDegerlendir: (id, dogru) => api.put(`/whatsapp/conversations/${id}/golge`, { dogru }),
  // V71.36 — bilgi bankası + performans panosu (beta)
  bilgi: () => api.get('/whatsapp/bilgi'),
  bilgiEkle: (data) => api.post('/whatsapp/bilgi', data),
  bilgiGuncelle: (id, data) => api.put(`/whatsapp/bilgi/${id}`, data),
  bilgiSil: (id) => api.delete(`/whatsapp/bilgi/${id}`),
  performans: (gun = 30) => api.get('/whatsapp/performans', { params: { gun } }),
};

// V71.38 — Sistem sağlığı (admin)
export const saglikAPI = { durum: () => api.get('/admin/saglik') };
// V71.43 — Yapay zekâ harcaması ve aylık sınırlar (admin)
export const yapayZekaAPI = { ozet: () => api.get('/admin/saglik/yapay-zeka'), kotaYaz: (data) => api.put('/admin/saglik/yapay-zeka/kota', data) };

// V71.52 — RateHawk deneme (yalnız admin; operasyona bağlı değil)
export const ratehawkAPI = {
  durum: () => api.get('/ratehawk/durum'),
  ulkeler: () => api.get('/ratehawk/ulkeler'),
  esitle: (data) => api.post('/ratehawk/statik/esitle', data),
  esitlemeDurumu: () => api.get('/ratehawk/statik/durum'),
  hedef: (q) => api.get('/ratehawk/hedef', { params: { q } }),
  ara: (data) => api.post('/ratehawk/ara', data),
  otel: (data) => api.post('/ratehawk/otel', data),
  onrez: (data) => api.post('/ratehawk/onrez', data),
  rezervasyon: (data) => api.post('/ratehawk/rezervasyon', data),
  liste: () => api.get('/ratehawk/rezervasyonlar'),
  getir: (id) => api.get(`/ratehawk/rezervasyonlar/${id}`),
  yenile: (id) => api.post(`/ratehawk/rezervasyonlar/${id}/yenile`),
  iptal: (id) => api.post(`/ratehawk/rezervasyonlar/${id}/iptal`),
  voucher: (id) => api.get(`/ratehawk/rezervasyonlar/${id}/voucher`, { responseType: 'blob' }),
  log: (por) => api.get('/ratehawk/log', { params: por ? { por } : {} }),
};

// V71.37 — Fuar fırsatları (Müşteri Fırsatları yetkisi)
export const fuarFirsatAPI = {
  liste: (gun = 120) => api.get('/fuar-firsat', { params: { gun } }),
  durumYaz: (fairId, companyId, data) => api.put(`/fuar-firsat/${fairId}/${companyId}`, data),
  firmalar: () => api.get('/fuar-firsat/firmalar'),
  kategoriYaz: (id, kategori) => api.put(`/fuar-firsat/firmalar/${id}`, { kategori }),
  kategoriOner: () => api.post('/fuar-firsat/firmalar/oner'),
};

// V71.36 — Beta özellikler: hepsi varsayılan kapalı; açmak yöneticinin e-postasına giden kodla, kapatmak anında
export const betaAPI = {
  durum: () => api.get('/beta/durum'),
  liste: () => api.get('/beta'),
  kodGonder: (anahtar) => api.post(`/beta/${anahtar}/kod`),
  ac: (anahtar, kod) => api.post(`/beta/${anahtar}/ac`, { kod }),
  kapat: (anahtar) => api.post(`/beta/${anahtar}/kapat`),
  ayarKaydet: (anahtar, data) => api.put(`/beta/${anahtar}/ayar`, data),
  ozetOnizle: (dep) => api.get('/beta/sabah-ozeti/onizleme', { params: { dep } }), // V71.41
  ozetDeneme: (dep) => api.post('/beta/sabah-ozeti/deneme', { dep }),
};

// V70.149 — MFA Vize Bilgisi (Hızlı Araçlar → Vize Bilgisi aracı; veri yerel DB'den, MFA'ya istek atılmaz)
export const visaMfaAPI = {
  getCountries: () => api.get('/visa-mfa/countries'),
  getCountry: (code) => api.get(`/visa-mfa/country/${code}`),
  sync: () => api.post('/visa-mfa/sync'),
  getSyncStatus: () => api.get('/visa-mfa/sync-status'),
};

// V71.17 — Hata Kayıtları (admin)
export const errorLogAPI = {
  list: (params) => api.get('/hata-kayitlari', { params }),
  get: (id) => api.get(`/hata-kayitlari/${id}`),
  update: (id, data) => api.patch(`/hata-kayitlari/${id}`, data),
};

// V71.32 — Sektör haberleri (RSS akışı)
export const newsAPI = {
  get: (id) => api.get(`/news/${id}`), // V71.41 — risk bildirimi bağlantısı
  list: (params) => api.get('/news', { params }),
  refresh: () => api.post('/news/yenile'),
  icerik: (id) => api.get(`/news/${id}/icerik`), // V71.33.1 — modal önizlemesi
};

export default api;
